import { createQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { useQuery } from "@tanstack/react-query";
import { odataFetch } from "../../lib/odata-fetch";
import {
  ActualProducedMaterial as ActualProducedMaterialModel,
  DM_MaterialTrackingUnit,
  MaterialTrackingUnit as MaterialTrackingUnitModel,
} from "../../odata/appu4dm/AppU4DMModel";
import {
  qActualProducedMaterial,
  QActualProducedMaterial,
} from "../../odata/appu4dm/QAppU4DM";
import { useWorkOrderOperationId } from "../work-order/use-work-order-operation-id";
import { mtuKeys } from "./mtu-keys";
import { usePersistedState } from "../../lib/usePersistedState";
import { useMachine } from "../machine/use-machine";

const STATE_KEY = "selectedMTUId";

export function useMTUs(all = false) {
  const { machine } = useMachine();
  const { workOrderOperationId } = useWorkOrderOperationId();
  const [selectedMTUId, setSelectedMTUId] = usePersistedState<
    string | undefined
  >(STATE_KEY, undefined);

  const { data, ...query } = useQuery({
    queryKey: mtuKeys.get(
      workOrderOperationId?.WorkOrderOperation_Id,
      workOrderOperationId?.Id
    ),
    queryFn: ({ meta }) =>
      fetchMTUs(workOrderOperationId!.WorkOrderOperation_Id!, meta!.token!),
    enabled: ({ meta }) =>
      !!(workOrderOperationId?.WorkOrderOperation_Id && meta?.token),
    select: (data) =>
      !all
        ? data?.value.filter((x) => x.Equipment === machine?.EquipmentNId)
        : data?.value,
  });

  const sortedMTUs = data?.sort(
    (a, b) => Date.parse(a.CreatedOn ?? "") - Date.parse(b.CreatedOn ?? "")
  );

  return {
    MTUs: sortedMTUs,
    selected:
      (selectedMTUId && data?.find((x) => x.Id === selectedMTUId)) || data?.[0],
    setSelected: (id: string) => setSelectedMTUId(id),
    ...query,
  };
}

type MaterialTrackingUnit = Pick<
  MaterialTrackingUnitModel,
  "Id" | "NId" | "Name" | "Code" | "CodeType"
>;

type DMMaterialTrackingUnit = Pick<
  DM_MaterialTrackingUnit,
  "MaterialTrackingUnit_Id" | "Weight" | "Volume"
> & { MaterialTrackingUnit: MaterialTrackingUnit };

type ActualProducedMaterial = Pick<
  ActualProducedMaterialModel,
  | "Id"
  | "CompletedQuantity"
  | "Equipment"
  | "NotWorkedQuantity"
  | "PartialWorkedQuantity"
  | "PausedQuantity"
  | "ReopenedQuantity"
  | "ScrappedQuantity"
  | "SentencedQuantity"
  | "SplitQuantity"
  | "UserId"
  | "ExecutionGroupPhaseId"
  | "WaitingForQualityQuantity"
  | "UnderQualityInspectionQuantity"
  | "WorkOrderOperations_Id"
  | "ToBeProducedMaterial_Id"
  | "DM_MaterialTrackingUnit_Id"
  | "WorkOrderStep_Id"
  | "CreatedOn"
> & { DM_MaterialTrackingUnit: DMMaterialTrackingUnit };

async function fetchMTUs(workOrderOperationId: string, token: string) {
  const builder = createQueryBuilderV4(
    "ActualProducedMaterial",
    new QActualProducedMaterial()
  )
    .filter(
      qActualProducedMaterial.WorkOrderOperations_Id.eq(
        workOrderOperationId
      ).and(qActualProducedMaterial.PartialWorkedQuantity.gt(0))
    )
    .expanding("DM_MaterialTrackingUnit", (b) =>
      b
        .expanding("MaterialTrackingUnit", (e) =>
          e.select("Id", "NId", "Name", "Code", "CodeType")
        )
        .select("MaterialTrackingUnit_Id", "Weight", "Volume")
    )
    .select(
      "Id",
      "CompletedQuantity",
      "Equipment",
      "NotWorkedQuantity",
      "PartialWorkedQuantity",
      "PausedQuantity",
      "ReopenedQuantity",
      "ScrappedQuantity",
      "SentencedQuantity",
      "SplitQuantity",
      "UserId",
      "ExecutionGroupPhaseId",
      "WaitingForQualityQuantity",
      "UnderQualityInspectionQuantity",
      "WorkOrderOperations_Id",
      "ToBeProducedMaterial_Id",
      "DM_MaterialTrackingUnit_Id",
      "WorkOrderStep_Id",
      "DM_MaterialTrackingUnit",
      "CreatedOn"
    );

  return odataFetch<ActualProducedMaterial>({ builder, token });
}
